/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
/*global vhCheck:true*/
// Extend From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import config from 'linced-app/config/environment';
import moment from 'moment';
// Internal Imports
import CountryCode from '@linc-technologies/linced-common/enums/country-code';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Options from '@linc-technologies/linced-common/enums/options';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { observes } from '@ember-decorators/object';

export default class ApplicationController extends Controller {
  @action
  setQuickSearchText(value) {
    this.quickSearchText = value;
  }

  @action
  setTermsAccepted(value) {
    this.termsAccepted = value;
  }

  @service
  features;

  @service
  layoutControls;

  @service
  options;

  @service
  permissions;

  // NOTE: injected under a different local name - the `quickSearch` action
  // below is a sibling class member and would otherwise shadow the service.
  @service('quick-search')
  quickSearchService;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  students;

  @service
  toaster;

  @service
  policyAcceptance;

  Jurisdiction = Jurisdiction;
  PersonType = PersonType;
  Scopes = Scopes;
  publicRoutes = config.APP.publicRoutes;
  @tracked
  termsAccepted = false;

  @computed('router.currentRouteName')
  get isPublicRoute() {
    let current = this.router.currentRouteName;
    return this.publicRoutes.includes(current);
  }

  @computed('session.school.name')
  get pageTitle() {
    let schoolName = this.get('session.school.name') ?? '';
    return `${schoolName} | Hero`;
  }

  @tracked isWideList = false;

  get leftSideBarLockedOpen() {
    if (this.isWideList) {
      return 'gt-lg';
    }
    return 'gt-md';
  }

  @computed('model.unmoderatedComments.@each.content')
  get unmoderatedCount() {
    if (isEmpty(this.model.unmoderatedComments)) {
      return 0;
    }
    return this.get('model.unmoderatedComments').filter((comment) => {
      return !isEmpty(comment.content);
    }).length;
  }

  @computed('isWideList', 'session.scopes.[]')
  get sidenavClasses() {
    let classes = ['site-nav-container'];
    if (!this.permissions.meet(Scopes.TENANT_ADMIN)) {
      // not an admin.  No further classes will be relevant.
      classes.push('no-left-nav');
    } else {
      if (this.isWideList) {
        classes.push('wide-list');
      }
    }

    return classes.join(' ');
  }

  // Local toggle state, set only by `toggleExpandedMenuItem`. Deliberately
  // not tied to the current route: it should only change on user click.
  @tracked
  expandedMenuItem = null;

  @tracked
  peopleSelectorDefault = [];

  @observes('router.currentURL')
  _closeSelectors() {
    // Close person/group selectors when transitioning to a new route
    this.closeAllSelectors();
  }

  @tracked
  quickSearchText = '';

  @computed('session.school.{countryCode,jurisdictionalId}')
  get applicationFormURL() {
    let school = this.session.school;
    // `session.school` starts null and is populated once the school record
    // loads, so this can be read before there is anything to build a URL from.
    if (!school) {
      return null;
    }
    let slug = CountryCode.getLabel(school.countryCode);
    return `${config.APP.enrolmentHost}/apply/${slug}/${school.jurisdictionalId}`;
  }

  applicationFormUnsaved = false;

  @alias('features.heroPremierEnabled')
  premierEnabled;

  @alias('features.linkedGoalsEnabled')
  linkedGoalsEnabled;

  bodyScrollable = true;

  @optionAlias('core:staffPermissions:classPlacementsForTeachers')
  classPlacementsForTeachers;

  @optionAlias('core:staffPermissions:hideNextYearsPlacementsForTeachers')
  hideNextYearsPlacementsForTeachers;

  get quickLinksClassPlacements() {
    return (
      this.classPlacementsForTeachers &&
      !this.hideNextYearsPlacementsForTeachers
    );
  }

  currentMonth = moment().month();
  currentYear = moment().format('YYYY');
  nextYear = moment().add(1, 'year').format('YYYY');
  prevYear = moment().subtract(1, 'year').format('YYYY');

  @alias('students.currentCount')
  currentCount;

  @alias('students.preenrolCount')
  preenrolCount;

  @alias('students.otherPreenrolCount')
  otherPreenrolCount;

  @alias('students.pendingCount')
  pendingCount;

  @alias('students.birthdayStudents')
  birthdayStudents;

  @optionAlias('core:staffPermissions:sickbayForTeachers')
  sickbayForTeachers;
  @optionAlias(Options.COMMUNITY_FEED_COMMENTS)
  communityNoticeCommentsEnabled;

  @computed('model.sickbayPage', 'sickbayForTeachers')
  get rightNavSickbayAccess() {
    // If there is no sickbay page, regular staff don't get access on the right nav
    // If the user scope is Tenant Admin or higher, they don't need this as they have access via left nav
    if (
      isEmpty(this.get('model.sickbayPage')) ||
      this.permissions.meet(Scopes.TENANT_ADMIN)
    ) {
      return false;
    }
    return this.sickbayForTeachers && this.features.heroSuiteEnabled;
  }

  @action
  openRightSideBar() {
    this.toggleProperty('rightSideBarOpen');
    if (this.model.events === null) {
      return;
    }
    // Update events when we have valid events data (ie we are admin)
    if (this.rightSideBarOpen && !this.permissions.meet(Scopes.TENANT_ADMIN)) {
      this.model.events.update().then(() => {
        if (this.model.events.length) {
          // Specifically load the previous and next year's groups in case the teacher has
          // access to a bookings event which includes previous or next year's groups
          this.store.query('group', {
            schoolId: this.get('session.school.id'),
            year: this.prevYear,
          });
          this.store.query('group', {
            schoolId: this.get('session.school.id'),
            year: this.nextYear,
          });
        }
      });
    }
  }

  @action
  logout() {
    this.session.logout('application');
  }

  @action
  toggleExpandedMenuItem(value, ev) {
    if (this.expandedMenuItem === value) {
      value = null;
    }
    this.expandedMenuItem = value;
    ev.stopPropagation();
  }

  @action
  quickSearch() {
    if (this.quickSearchText) {
      let searchText = this.quickSearchText;
      this.rightSideBarOpen = false;
      this.quickSearchText = '';

      this.quickSearchService.search(searchText);
    }
  }

  @action
  stayOpen(ev) {
    ev.stopPropagation();
  }

  @action
  closeSelectors() {
    this.closeAllSelectors();
  }

  @action
  acceptTerms() {
    return this.policyAcceptance.acceptTerms();
  }

  init() {
    super.init(...arguments);

    let spinner = document.querySelector('#preloader');
    spinner.parentElement.removeChild(spinner);

    // Check for iOS `vh` offset and set CSS variable as required
    vhCheck();

    this.layoutControls.setup();
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.layoutControls.teardown();
  }

  setPeopleSelectorDefault(people = []) {
    this.peopleSelectorDefault = people;
  }

  closeAllSelectors() {
    let body = document.querySelector('body');
    if (body) {
      body.className = body.className.replace(/ no-scroll/g, '');
    }
  }

  // unprocessedApplicationCount returns the number of enrolment applications
  // that are currently marked as unprocessed.
  @alias('model.unprocessedEnrolmentApplications.length')
  unprocessedApplicationCount;

  @tracked
  rightSideBarOpen;

  // refreshUnprocessedApplications provides a function to refresh the model
  // query on demand.
  refreshUnprocessedApplications() {
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      // Only Tenant admins + can see enrolment applications
      if (this.model.unprocessedEnrolmentApplications === null) {
        return;
      }
      this.model.unprocessedEnrolmentApplications.update();
    }
  }
}
